import { useEffect, useState } from "react";
import { Play, Film, Instagram, Youtube, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph, SITE, PUBLISHER_REF, videoObject } from "../lib/schema";

/**
 * Consolidated video hub — surfaces every video artefact managed by the CMS in
 * one place:
 *
 *   1. Long-form videos & press features (Media collection, type=video)
 *   2. Patient testimonial videos (Testimonials with video_id)
 *   3. Shorts & Reels — YouTube Shorts + Instagram Reels
 *
 * Each section renders in the aspect ratio native to its content
 * (16:9 for long-form / testimonials, 9:16 for shorts).
 */

// Extract a YouTube video id from either a full URL or a raw 11-char id.
const ytId = (raw) => {
  if (!raw) return "";
  if (/^[\w-]{6,15}$/.test(raw)) return raw;
  const m =
    raw.match(/youtube\.com\/shorts\/([\w-]{6,})/) ||
    raw.match(/youtube\.com\/watch\?v=([\w-]{6,})/) ||
    raw.match(/youtu\.be\/([\w-]{6,})/);
  return m ? m[1] : "";
};

const YouTubeEmbed = ({ id, title, className = "" }) => (
  <div className={`relative w-full ${className}`} style={{ aspectRatio: "16/9" }}>
    <iframe
      className="absolute inset-0 w-full h-full rounded-2xl border border-border"
      src={`https://www.youtube.com/embed/${id}?rel=0`}
      title={title}
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  </div>
);

// Vertical short — YouTube Shorts + Instagram Reels natively render 9:16.
const ShortEmbed = ({ item }) => {
  const isIg = item.platform === "instagram_reel";
  return (
    <div className="relative w-full mx-auto rounded-2xl overflow-hidden border border-border bg-black" style={{ aspectRatio: "9/16", maxWidth: 340 }}>
      <iframe
        className="absolute inset-0 w-full h-full"
        src={item.embed_url}
        title={item.title || item.caption || "Short video"}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        scrolling="no"
      />
      {/* Corner platform badge */}
      <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/70 backdrop-blur text-white text-[10px] uppercase tracking-[0.18em]">
        {isIg ? <Instagram size={12} /> : <Youtube size={12} />}
        {isIg ? "Reel" : "Short"}
      </div>
    </div>
  );
};

const Videos = () => {
  usePageMeta({
    title: "Videos & Reels | Dr. Shantanu Kumar Gupta",
    path: "/videos",
    description:
      "Watch educational videos, YouTube Shorts, Instagram Reels and patient testimonial videos from Dr. Shantanu Kumar Gupta — Ophthalmologist, Varanasi.",
  });

  const [media, setMedia] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [shorts, setShorts] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/media").then((r) => r.data).catch(() => []),
      api.get("/testimonials").then((r) => r.data).catch(() => []),
      api.get("/shorts").then((r) => r.data).catch(() => []),
    ]).then(([m, t, s]) => {
      setMedia((m || []).filter((x) => x.type === "video" && x.link));
      setTestimonials((t || []).filter((x) => x.video_id));
      setShorts((s || []).filter((x) => x.embed_url));
    });
  }, []);

  const isEmpty = !media.length && !testimonials.length && !shorts.length;

  // Build VideoObject schema AFTER state is populated so Google indexes
  // every known Short/Reel/testimonial.  Placed here (not at the top of
  // the component) to avoid the temporal-dead-zone `Cannot access
  // 'shorts' before initialization` crash.
  usePageJsonLd(
    graph([
      breadcrumb([{ name: "Home", path: "/" }, { name: "Videos", path: "/videos" }]),
      ...shorts
        .slice(0, 20)
        .map((s) =>
          videoObject({
            id: `${s.platform || "video"}-${s.video_id || s.id || s._id}`,
            pagePath: "/videos",
            name: s.title || `${(s.platform || "Video").toUpperCase()} · Shantanu Netralaya`,
            description: s.caption || s.description,
            videoRef: s.video_id || s.url || s.embed_url,
            thumbnailUrl: s.thumbnail_url || s.thumbnail,
            uploadDate: s.uploaded_at || s.updated_at,
          })
        )
        .filter(Boolean),
      ...testimonials
        .slice(0, 20)
        .map((t) =>
          videoObject({
            id: `testimonial-${t.video_id || t.id || t.name}`.toLowerCase().replace(/\s+/g, "-"),
            pagePath: "/videos",
            name: t.video_title || `${t.name} — ${t.procedure} testimonial`,
            description: t.quote || `Patient testimonial for ${t.procedure} at Shantanu Netralaya, Varanasi.`,
            videoRef: t.video_id,
            uploadDate: t.date || t.updated_at,
          })
        )
        .filter(Boolean),
      ...media
        .slice(0, 20)
        .map((m) =>
          videoObject({
            id: `media-${m.id || m.slug || m._id || m.title}`.toLowerCase().replace(/\s+/g, "-"),
            pagePath: "/videos",
            name: m.title || "Shantanu Netralaya video",
            description: m.excerpt || m.summary || m.description,
            videoRef: m.link,
            thumbnailUrl: m.thumbnail || m.cover,
            uploadDate: m.date || m.updated_at,
          })
        )
        .filter(Boolean),
    ])
  );

  return (
    <div data-testid="videos-page">
      {/* Hero */}
      <section className="pt-20 pb-8 container-page">
        <div className="overline mb-4 flex items-center gap-2">
          <Film size={14} strokeWidth={1.5} /> Video Library
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Educational videos, patient stories & 60-second explainers.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
          From long-form conference talks to YouTube Shorts and Instagram Reels — a single home for every video Dr. Shantanu has ever recorded.
        </p>
      </section>

      {isEmpty && (
        <section className="container-page pb-24 text-center text-muted-foreground text-sm">
          Videos are being uploaded. Please check back shortly.
        </section>
      )}

      {/* Long-form videos */}
      {media.length > 0 && (
        <section className="py-10 container-page" data-testid="videos-longform">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif text-2xl sm:text-3xl">Talks & interviews</h2>
            <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{media.length} videos</span>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {media.map((m) => {
              const id = ytId(m.link);
              return (
                <a
                  key={m.title}
                  href={m.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                  data-testid={`videos-media-${m.title.slice(0, 20).replace(/\s/g, "-").toLowerCase()}`}
                >
                  {id ? (
                    <YouTubeEmbed id={id} title={m.title} />
                  ) : (
                    <div className="relative w-full rounded-2xl border border-border bg-[hsl(var(--secondary))] flex items-center justify-center" style={{ aspectRatio: "16/9" }}>
                      <Play size={36} strokeWidth={1} className="text-[hsl(var(--primary))]" />
                    </div>
                  )}
                  <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
                      {m.outlet} · {m.date}
                    </div>
                    <h3 className="font-serif text-xl mt-1.5 group-hover:text-[hsl(var(--primary))] transition-colors">
                      {m.title}
                    </h3>
                    {m.summary && <p className="text-sm text-muted-foreground mt-2">{m.summary}</p>}
                  </div>
                </a>
              );
            })}
          </div>
        </section>
      )}

      {/* Patient testimonial videos */}
      {testimonials.length > 0 && (
        <section className="py-10 container-page" data-testid="videos-testimonials">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif text-2xl sm:text-3xl">Patient stories</h2>
            <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{testimonials.length} stories</span>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((t) => {
              const id = ytId(t.video_id);
              if (!id) return null;
              return (
                <div key={t.name} data-testid={`videos-testimonial-${t.name.replace(/\s/g, "-").toLowerCase()}`}>
                  <YouTubeEmbed id={id} title={t.video_title || `${t.name} — ${t.procedure}`} />
                  <div className="mt-4">
                    <div className="font-serif text-lg">{t.name}</div>
                    <div className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--accent))] mt-1">
                      {t.procedure}{t.location ? ` · ${t.location}` : ""}
                    </div>
                    {t.quote && <p className="text-sm text-muted-foreground mt-3 line-clamp-3">“{t.quote}”</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Shorts + Reels — 9:16 vertical grid */}
      {shorts.length > 0 && (
        <section className="py-14 container-page" data-testid="videos-shorts">
          <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl">Shorts & Reels</h2>
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground mt-2">
                Quick 60-second answers to the questions patients ask most.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <a href="https://www.youtube.com/@drshantanukumar" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[hsl(var(--primary))]">
                <Youtube size={14} /> YouTube <ExternalLink size={11} />
              </a>
              <span className="opacity-40">·</span>
              <a href="https://www.instagram.com/dr.shantanu.gupta" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[hsl(var(--primary))]">
                <Instagram size={14} /> Instagram <ExternalLink size={11} />
              </a>
            </div>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {shorts.map((s) => (
              <div
                key={s.id}
                className="group flex flex-col"
                data-testid={`videos-short-${s.video_id}`}
              >
                <ShortEmbed item={s} />
                {s.caption && (
                  <p className="mt-3 text-sm text-muted-foreground group-hover:text-foreground transition-colors leading-snug">
                    {s.caption}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Videos;
